// src/components/settings/PortalLogins.jsx — a work number's myhome.ge / ss.ge logins (scheduler 0039,
// owner 2026-10-10: "we must save these platforms' passwords linked to that number … the agent sees
// those passwords and logs in").
//
// Two renderings of one panel:
//   • PhoneLoginsPanel — the logins of ONE number (Admin → Work Phone Numbers, the agent modal).
//   • MyPortalPasswords — Settings → Passwords: every number the viewer HOLDS.
// Access is the database's: the RPCs answer only for numbers the viewer may see (company, the number's
// office manager, or its current holder), so this component never decides who sees a password.
import { useState, useEffect } from 'react'
import { t, tLog, getLanguage } from '../../lib/i18n'
import { getPhoneLogins, getMyPhoneLogins, setPhoneLogin } from '../../db/index.js'
import { fmtPhone } from '../../lib/phone.js'

// Always offered, in this order; a recorded korter/other login is shown after them.
const MAIN = [
  { platform: 'myhome', label: 'myhome.ge', url: 'https://www.myhome.ge/' },
  { platform: 'ss',     label: 'ss.ge',     url: 'https://home.ss.ge/' },
]
const LABEL = { myhome: 'myhome.ge', ss: 'ss.ge', korter: 'korter.ge' }

const fmtDate = iso => iso
  ? new Date(iso).toLocaleDateString(getLanguage() === 'ka' ? 'ka-GE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : ''

function CopyBtn({ value }) {
  const [done, setDone] = useState(false)
  if (!value) return null
  return (
    <button type="button" onClick={() => navigator.clipboard?.writeText(value).then(() => { setDone(true); setTimeout(() => setDone(false), 1200) })}
      className="text-[11px] px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground">
      {done ? '✓' : t('portal_copy')}
    </button>
  )
}

function LoginRow({ phoneId, spec, row, canEdit, onChanged }) {
  const [edit, setEdit] = useState(false)
  const [show, setShow] = useState(false)
  const [login, setLogin] = useState(row?.login || '')
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const label = spec?.label || LABEL[row?.platform] || row?.platform_label || row?.platform

  const save = async () => {
    setBusy(true); setErr('')
    try {
      // An empty password box keeps the stored one; the agent only types a password to CHANGE it.
      await setPhoneLogin(phoneId, row?.platform || spec.platform,
        { login, password: pw === '' ? null : pw, platformLabel: row?.platform_label || null })
      setEdit(false); setPw('')
      await onChanged?.()
    } catch (x) { setErr(x.message || String(x)) }
    finally { setBusy(false) }
  }

  const inp = 'w-full rounded-md border border-input px-2 py-1 text-sm bg-background'
  return (
    <div className="rounded-md bg-muted/40 px-3 py-2 space-y-1" data-portal-login={row?.platform || spec?.platform}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{label}</span>
        {spec?.url && <a href={spec.url} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline">{t('portal_open_site')} ↗</a>}
        {row?.updated_at && <span className="text-[11px] text-muted-foreground ml-auto">{tLog('portal_updated', { date: fmtDate(row.updated_at), who: row.updated_by || '' })}</span>}
        {canEdit && !edit && (
          <button type="button" onClick={() => { setEdit(true); setLogin(row?.login || ''); setPw('') }}
            className={`text-xs text-muted-foreground hover:text-foreground ${row?.updated_at ? '' : 'ml-auto'}`}>
            {row ? t('portal_edit') : `＋ ${t('portal_add')}`}
          </button>
        )}
      </div>

      {!edit && row && (
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-2 gap-y-1 text-sm">
          <span className="text-muted-foreground text-xs">{t('portal_login')}</span>
          <span className="font-mono truncate">{row.login || '—'}</span>
          <CopyBtn value={row.login} />
          <span className="text-muted-foreground text-xs">{t('portal_password')}</span>
          <span className="font-mono truncate">
            {row.password ? (show ? row.password : '••••••••') : '—'}
            {row.password && (
              <button type="button" onClick={() => setShow(s => !s)} className="ml-2 text-[11px] text-muted-foreground hover:text-foreground">
                {show ? t('portal_hide') : t('portal_show')}
              </button>
            )}
          </span>
          <CopyBtn value={row.password} />
        </div>
      )}
      {!edit && !row && <div className="text-xs text-muted-foreground italic">{t('portal_none')}</div>}

      {edit && (
        <div className="space-y-1.5">
          <input value={login} onChange={e => setLogin(e.target.value)} placeholder={t('portal_login_ph')}
            autoComplete="off" className={inp} />
          <input value={pw} onChange={e => setPw(e.target.value)} type="text"
            placeholder={row?.password ? t('portal_password_keep_ph') : t('portal_password')}
            autoComplete="new-password" spellCheck={false} className={`${inp} font-mono`} />
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={save}
              className="text-xs px-3 py-1 rounded-md bg-primary text-primary-foreground disabled:opacity-50">{busy ? '…' : t('portal_save')}</button>
            <button type="button" onClick={() => { setEdit(false); setErr('') }} className="text-xs px-2 py-1 text-muted-foreground">{t('portal_cancel')}</button>
          </div>
        </div>
      )}
      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  )
}

// One number's logins. `logins` = live rows for this number (from phone_logins_for / my_phone_logins).
export function PhoneLoginsPanel({ phoneId, logins = [], canEdit = true, onChanged }) {
  const extra = logins.filter(l => !MAIN.some(m => m.platform === l.platform))
  return (
    <div className="space-y-1.5" data-portal-logins={phoneId}>
      {MAIN.map(m => (
        <LoginRow key={m.platform + (logins.find(l => l.platform === m.platform)?.id || '')} phoneId={phoneId} spec={m}
          row={logins.find(l => l.platform === m.platform)} canEdit={canEdit} onChanged={onChanged} />
      ))}
      {extra.map(l => <LoginRow key={l.id} phoneId={phoneId} row={l} canEdit={canEdit} onChanged={onChanged} />)}
    </div>
  )
}

// Loads its own data — used inside the number-details window.
export function PhoneLoginsLoader({ phoneId }) {
  const [rows, setRows] = useState(null)
  const [err, setErr] = useState('')
  const load = () => getPhoneLogins([phoneId]).then(setRows).catch(e => { setErr(e.message || String(e)); setRows([]) })
  useEffect(() => { load() }, [phoneId])
  if (rows === null) return <div className="text-xs text-muted-foreground">…</div>
  return (<>
    <PhoneLoginsPanel phoneId={phoneId} logins={rows} onChanged={load} />
    {err && <div className="text-xs text-red-600">{err}</div>}
  </>)
}

// Settings → Passwords. "Your passwords on myhome.ge / ss.ge".
export function MyPortalPasswords() {
  const [nums, setNums] = useState(null)
  const [err, setErr] = useState('')
  const load = () => getMyPhoneLogins().then(setNums).catch(e => { setErr(e.message || String(e)); setNums([]) })
  useEffect(() => { load() }, [])
  return (
    <div className="rounded-xl border border-border p-5 space-y-3 max-w-[60rem]" data-my-portal-passwords>
      <div>
        <div className="font-semibold">{t('portal_my_title')}</div>
        <div className="text-sm text-muted-foreground">{t('portal_my_sub')}</div>
      </div>
      {nums === null && <div className="text-sm text-muted-foreground">…</div>}
      {nums && nums.length === 0 && !err && <div className="text-sm text-muted-foreground italic">{t('portal_my_no_number')}</div>}
      {(nums || []).map(n => (
        <div key={n.phone_number_id} className="space-y-1.5">
          <div className="text-sm font-mono font-medium">{fmtPhone(n.number)}</div>
          <PhoneLoginsPanel phoneId={n.phone_number_id} logins={n.logins || []} onChanged={load} />
        </div>
      ))}
      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  )
}
